/**
 * Turning a failed request into something we can say to a customer.
 *
 * Suspire's own brands work inside Terminal, so an outage of ours is visible to
 * their customers too. "Error", "failed" and a bare status code read as *our*
 * product breaking; the same moment described as "we're rolling out an update"
 * reads as maintenance. Nothing here is a euphemism for data loss — it is the
 * difference between naming the cause and naming the blame.
 *
 * Every user-facing string lives in TERMINAL_COPY and is asserted by
 * errorTaxonomy.test.js to contain none of the alarming vocabulary. Add new copy
 * there, not inline at a call site.
 */

import { isServerBusyError } from './serverBusy';
import { parseValidationErrors, extractErrorBody } from './validationErrors';

/**
 * The kinds of failure we distinguish. Anything we cannot place lands on
 * `unknown`, which is deliberately reassuring rather than apologetic.
 */
export const ERROR_KIND = {
  BUSY: 'busy',
  DEPLOYING: 'deploying',
  RATE_LIMITED: 'rateLimited',
  QUOTA: 'quota',
  NOT_FOUND: 'notFound',
  FORBIDDEN: 'forbidden',
  VALIDATION: 'validation',
  CONFLICT: 'conflict',
  OFFLINE: 'offline',
  UNKNOWN: 'unknown',
};

export const TERMINAL_COPY = {
  deploying: {
    title: "We're rolling out an update",
    message:
      "We're deploying new features and improving your experience. This usually " +
      "takes under a minute — we'll bring your data back automatically.",
  },
  busy: {
    title: 'A lot of people are using this right now',
    message:
      "Terminal is handling a burst of activity. Hang tight — we're getting your " +
      'data and will refresh this section the moment it’s ready.',
  },
  rateLimited: {
    title: 'Catching our breath',
    message:
      "Terminal is pacing requests to keep things fast for everyone. We'll pick " +
      'this back up in a moment.',
  },
  quota: {
    title: "You've used today's AI allowance",
    message:
      'Your daily AI capacity resets at midnight IST. Everything else in Terminal ' +
      'is working normally.',
  },
  offline: {
    title: "We can't reach Terminal from this device",
    message:
      "This usually clears on its own. Check your connection — we'll keep trying " +
      'in the background.',
  },
  notFound: {
    title: "We couldn't find this",
    message:
      'This record may have been moved, merged, or renamed. Head back to the list ' +
      'to pick it up from there.',
  },
  forbidden: {
    title: 'This section needs different access',
    message:
      "Your account doesn't include this view yet. Your admin can enable it for you.",
  },
  conflict: {
    title: 'This is already running',
    message:
      "An export for this is in progress. We'll have it ready shortly — no need to " +
      'start another.',
  },
  validation: {
    title: 'A few details need a second look',
    message: "Check the highlighted fields and we'll get this through.",
  },
  unknown: {
    title: 'This section is taking a moment',
    message:
      "We're working on getting your data. The rest of Terminal is available while " +
      'we do.',
  },
};

/**
 * Copy for a write that could not reach the server.
 *
 * Deliberately does not claim the write failed — during a deploy the request may
 * never have been sent, and during a timeout it may have landed. "Not yet" is
 * the only honest tense.
 */
export const WRITE_BLOCKED_COPY = {
  title: 'Hold that thought',
  message:
    "We couldn't save that just yet — your details are still here, and you can " +
    'try again in a moment.',
};

/** Statuses that are worth trying again without the user changing anything. */
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

// ─── Retry-After ──────────────────────────────────────────────────────────────

/**
 * Never *wait* longer than this on a server's say-so — past half a minute an
 * automatic retry stops feeling like recovery and starts feeling frozen, and the
 * user is better served by a button.
 *
 * This caps the wait, not the parse: the true figure still reaches the UI, which
 * may legitimately want to say "try again in 4 minutes".
 */
export const MAX_RETRY_AFTER_WAIT_MS = 30000;

/**
 * Parse a `Retry-After` header, which RFC 9110 allows in two forms: a count of
 * seconds, or an HTTP date. Returns the full delay in milliseconds, or null when
 * the header is absent or unintelligible.
 *
 * Deliberately unclamped — see `retryAfterWait` for the value to actually sleep.
 */
export function parseRetryAfter(headerValue, now = Date.now()) {
  if (headerValue === null || headerValue === undefined || headerValue === '') return null;

  const raw = String(headerValue).trim();

  // Delay-seconds form.
  if (/^\d+$/.test(raw)) {
    return Number(raw) * 1000;
  }

  // HTTP-date form.
  const when = Date.parse(raw);
  if (Number.isNaN(when)) return null;

  return Math.max(0, when - now);
}

/**
 * How long to actually sleep before an automatic retry, given a parsed
 * `Retry-After`. Null stays null so the caller falls back to its own backoff.
 */
export function retryAfterWait(retryAfterMs) {
  if (retryAfterMs === null || retryAfterMs === undefined) return null;
  return Math.min(Math.max(0, retryAfterMs), MAX_RETRY_AFTER_WAIT_MS);
}

// ─── Is nginx even up? ────────────────────────────────────────────────────────

/**
 * `busy` and `deploying` are indistinguishable by status — both surface as a 502
 * or as a fetch that never got a response. The difference is whether nginx is
 * answering at all, and `/health/` answers that in one cheap unauthenticated
 * call.
 *
 * `/health/` does not touch the database, so it is worthless as a "can I load
 * data" probe — but that is exactly what makes it a clean liveness signal: a 200
 * means nginx and gunicorn are up and the failure is load, not a deploy.
 *
 * Do not use `/readiness/`: nginx has no location block for it and 301s it to
 * `/api/`, so the browser never reaches Django.
 */
const HEALTH_PROBE_TIMEOUT_MS = 3000;
const HEALTH_CACHE_MS = 10000;

let healthCache = { at: 0, alive: null };
let healthInFlight = null;

/** Discards the cached probe result. Exported for tests. */
export function resetHealthProbe() {
  healthCache = { at: 0, alive: null };
  healthInFlight = null;
}

/**
 * True when nginx answered, false when it did not, null when we could not tell.
 *
 * Single-flight and cached for 10s: during an outage a dozen mounted panels all
 * ask at once, and a shared nginx rate-limit bucket is the last thing to add a
 * dozen requests to.
 */
export async function probeServerAlive(now = Date.now()) {
  if (healthCache.alive !== null && now - healthCache.at < HEALTH_CACHE_MS) {
    return healthCache.alive;
  }
  if (healthInFlight) return healthInFlight;

  const base = process.env.REACT_APP_API_BASE_URL || '';

  healthInFlight = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT_MS);
    try {
      const res = await fetch(`${base}/health/`, {
        method: 'GET',
        signal: controller.signal,
        // No Authorization header: /health/ is AllowAny, and sending a stale
        // token here could trip the session-expired flow off a liveness check.
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      return res.ok;
    } catch {
      // Aborted or rejected outright — from the browser's point of view nginx
      // is not answering.
      return false;
    } finally {
      clearTimeout(timer);
    }
  })();

  try {
    const alive = await healthInFlight;
    healthCache = { at: Date.now(), alive };
    return alive;
  } finally {
    healthInFlight = null;
  }
}

// ─── Classification ───────────────────────────────────────────────────────────

/** Pull a status off whatever shape the caller threw. */
function statusOf(err, response) {
  if (response && typeof response.status === 'number') return response.status;
  if (!err) return 0;
  if (typeof err === 'number') return err;
  const status =
    err.status ??
    (err.response && err.response.status) ??
    (err.data && err.data.status);
  return typeof status === 'number' ? status : 0;
}

/** True when the browser never got a response at all. */
function isNetworkReject(err) {
  if (!err) return false;
  const message = String(err.message || '');
  return (
    err instanceof TypeError ||
    err.status === 0 ||
    /failed to fetch|networkerror|network request failed|load failed/i.test(message)
  );
}

function isAbortLike(err) {
  return Boolean(err) && (err.name === 'AbortError' || err.name === 'TimeoutError');
}

/**
 * The first human-readable sentence the backend gave us, or null.
 *
 * Roughly 1400 call sites in the Django app return `{"error": "<a sentence>"}`,
 * already written for a person — those are better than anything generic we could
 * substitute. But some return a code (`VALIDATION_ERROR`, `SERVER_BUSY`), which
 * is not; a token with no spaces is the tell.
 */
function backendSentence(body) {
  if (!body) return null;

  const candidates = [body.error, body.message, body.detail];
  for (const value of candidates) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    // SCREAMING_SNAKE codes and single tokens are machine-facing.
    if (!/\s/.test(trimmed)) continue;
    if (/^[A-Z0-9_]+$/.test(trimmed)) continue;
    return trimmed;
  }
  return null;
}

function copyFor(kind) {
  return TERMINAL_COPY[kind] || TERMINAL_COPY.unknown;
}

function build(kind, overrides = {}) {
  const base = copyFor(kind);
  return {
    kind,
    title: base.title,
    message: base.message,
    retryable: false,
    retryAfterMs: null,
    status: 0,
    detail: null,
    raw: null,
    ...overrides,
  };
}

/**
 * Describe a failed request.
 *
 * @param {unknown} err        Whatever was thrown — an Error carrying `status`/`data`,
 *                             a TypeError from fetch, an AbortError, or a status number.
 * @param {object} [context]
 * @param {Response} [context.response]  The response, when the caller still has it.
 * @param {object}   [context.body]      Pre-read body from `readErrorBody`.
 * @param {boolean}  [context.serverAlive] Result of `probeServerAlive`, when known.
 *                                         Distinguishes `busy` from `deploying`.
 * @param {number}   [context.consecutiveNetworkFailures]
 *                             How many attempts in a row got no response at all.
 *                             Two or more with nginx unreachable means a deploy.
 * @returns {{kind: string, title: string, message: string, retryable: boolean,
 *            retryAfterMs: number|null, status: number, detail: object|null, raw: unknown}}
 */
export function classifyError(err, context = {}) {
  const {
    response = null,
    serverAlive = null,
    consecutiveNetworkFailures = 0,
  } = context;

  const body = context.body !== undefined ? context.body : extractErrorBody(err);
  const status = statusOf(err, response);
  const raw = err;

  // A request the user cancelled is not a failure and must never be rendered.
  if (err && (err.aborted === true || err.name === 'RequestAbortedError')) {
    return build(ERROR_KIND.UNKNOWN, { status, raw, retryable: false, aborted: true });
  }

  // ── No response at all ──────────────────────────────────────────────────────
  // The browser being offline is the one case we can state with certainty, and
  // it needs different advice (check your connection) from an outage of ours.
  if (isNetworkReject(err) || isAbortLike(err)) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return build(ERROR_KIND.OFFLINE, { status: 0, raw, retryable: true });
    }

    // nginx confirmed down, or repeated silence — that signature is a deploy.
    // A saturated gunicorn still answers *through* nginx with a 502, so pure
    // silence means the front door itself is gone.
    const looksLikeDeploy = serverAlive === false || consecutiveNetworkFailures >= 2;
    return build(looksLikeDeploy ? ERROR_KIND.DEPLOYING : ERROR_KIND.BUSY, {
      status: 0,
      raw,
      retryable: true,
    });
  }

  // ── 429: two different meanings ────────────────────────────────────────────
  if (status === 429) {
    const header = response && response.headers ? response.headers.get('Retry-After') : null;
    const retryAfterMs = parseRetryAfter(header);

    if (retryAfterMs !== null) {
      return build(ERROR_KIND.RATE_LIMITED, { status, raw, retryable: true, retryAfterMs });
    }
    // A `usage` object with no Retry-After is the daily AI allowance, which
    // cannot succeed again until midnight IST. Offering a retry would be a lie.
    if (body && body.usage) {
      return build(ERROR_KIND.QUOTA, { status, raw, retryable: false, detail: body.usage });
    }
    // Ambiguous: a retry that fails is gentler than a false "you're out for today".
    return build(ERROR_KIND.RATE_LIMITED, { status, raw, retryable: true, retryAfterMs: 60000 });
  }

  // ── Overload ───────────────────────────────────────────────────────────────
  // 503 is also what nginx returns when its rate limiter trips, with an HTML
  // body — indistinguishable here from a genuine outage, and retryable either
  // way, so it is safe to treat them alike.
  if (RETRYABLE_STATUSES.has(status) || isServerBusyError(err)) {
    const looksLikeDeploy = serverAlive === false;
    return build(looksLikeDeploy ? ERROR_KIND.DEPLOYING : ERROR_KIND.BUSY, {
      status,
      raw,
      retryable: true,
    });
  }

  // ── Definite answers from the application ──────────────────────────────────
  if (status === 404) {
    return build(ERROR_KIND.NOT_FOUND, {
      status,
      raw,
      message: backendSentence(body) || TERMINAL_COPY.notFound.message,
    });
  }

  if (status === 403) {
    return build(ERROR_KIND.FORBIDDEN, { status, raw });
  }

  if (status === 409) {
    return build(ERROR_KIND.CONFLICT, {
      status,
      raw,
      message: backendSentence(body) || TERMINAL_COPY.conflict.message,
    });
  }

  if (body && body.error === 'VALIDATION_ERROR') {
    const parsed = parseValidationErrors(err);
    return build(ERROR_KIND.VALIDATION, {
      status: status || 400,
      raw,
      detail: parsed,
      message: parsed.message || TERMINAL_COPY.validation.message,
    });
  }

  if (status === 400 || status === 422) {
    const parsed = parseValidationErrors(err);
    if (parsed.isValidationError) {
      return build(ERROR_KIND.VALIDATION, {
        status,
        raw,
        detail: parsed,
        message: parsed.message || TERMINAL_COPY.validation.message,
      });
    }
    return build(ERROR_KIND.VALIDATION, {
      status,
      raw,
      message: backendSentence(body) || TERMINAL_COPY.validation.message,
    });
  }

  // ── 500 and anything unplaced ──────────────────────────────────────────────
  // A real 500 comes back as text/plain "Internal Server Error", so there is no
  // body to quote and nothing the user can do. Reassure rather than apologise,
  // and do not offer a retry that would just burn one of 16 gunicorn slots.
  if (status === 500) {
    return build(ERROR_KIND.UNKNOWN, { status, raw, retryable: true });
  }

  return build(ERROR_KIND.UNKNOWN, {
    status,
    raw,
    retryable: status === 0,
  });
}

const errorTaxonomy = {
  classifyError,
  parseRetryAfter,
  retryAfterWait,
  probeServerAlive,
  resetHealthProbe,
  ERROR_KIND,
  TERMINAL_COPY,
  WRITE_BLOCKED_COPY,
};

export default errorTaxonomy;
